import Loader from '@bugbug/core/components/Loader';
import Tooltip from '@bugbug/core/components/Tooltip';
import { STEP_TYPE } from '@bugbug/core/constants/steps';
import { isFailedStatus } from '@bugbug/core/types/base';
import { Formik, useFormikContext } from 'formik';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router';
import { useEvent, useUpdateEffect } from 'react-use';
import * as Yup from 'yup';

import type { TemporaryStep } from '~/modules/steps/steps.types';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { StepRun, TestRun } from '@bugbug/core/types/tests';
import type { SideEffect } from '@bugbug/core/types/utils';
import ServerErrorInfo from '~/components/ServerErrorInfo';
import useLeavePagePrompt from '~/hooks/useLeavePagePrompt';
import useModal from '~/hooks/useModal';
import { selectIsFreePlan } from '~/modules/organization/organization.selectors';
import { useSaveTemporaryStep } from '~/modules/steps/hooks/useSaveTemporaryStep/useSaveTemporaryStep';
import { useUpdateStep } from '~/modules/steps/hooks/useUpdateStep/useUpdateStep';
import { useGetStepDetailsQuery } from '~/modules/steps/steps.api';
import { DefaultStepSchema, StepSchema } from '~/modules/steps/steps.schemas';
import { selectTemporaryStep } from '~/modules/steps/steps.selectors';
import { isTemporaryStep } from '~/modules/steps/steps.types';
import { getInitialValues } from '~/modules/steps/steps.utils';
import { useAppSelector } from '~/modules/store';
import { selectStepRun } from '~/modules/testRun/testRun.selectors';
import {
  getExtractedErrorMessages,
  getExtractedErrors,
  isBadRequestError,
} from '~/services/api/utils';
import { addProtocolWhenNotExists } from '~/utils/url';

import StepRunMetadata from '../StepRunMetadata';

import { STEP_UNSAVED_CHANGES } from './StepDetails.constants';
import * as S from './StepDetails.styled';
import { StepDetailsEditor } from './StepDetailsEditor';

interface StepDetailsProps {
  groupId: Group['id'];
  stepId: Step['id'];
  onClose: SideEffect<void>;
  context: 'component' | 'test' | 'testRun';
  testRunId?: TestRun['id'];
  readOnly?: boolean;
}

export const StepDetails = memo(
  ({ context = 'test', onClose, stepId, testRunId, readOnly, groupId }: StepDetailsProps) => {
    const { t } = useTranslation();
    const isFreePlan = useAppSelector(selectIsFreePlan) as boolean;
    const tempStep = useAppSelector(selectTemporaryStep);
    const [saveTemporaryStep] = useSaveTemporaryStep();
    const [saveStep] = useUpdateStep();

    const { data, isLoading, isError, refetch, error } = useGetStepDetailsQuery(
      {
        id: stepId,
        groupId,
        testRunId: context !== 'test' ? testRunId : undefined,
      },
      {
        skip: !stepId || tempStep?.id === stepId,
      },
    );

    const step = tempStep ?? data;
    const isTemporary = !!step && isTemporaryStep(step);
    const stepGroupId = tempStep?.groupId ?? groupId;
    const atIndex = tempStep?.atIndex ?? 0;
    const getValidationSchema = useCallback(
      () => Yup.lazy((values) => StepSchema[values.type] || DefaultStepSchema),
      [],
    );

    const handleSubmit = useCallback(
      async (values, { resetForm }) => {
        const schema = StepSchema[values.type] || DefaultStepSchema;
        const newValues = schema.cast({ ...values, url: addProtocolWhenNotExists(values.url) });

        try {
          if (isTemporary) {
            await saveTemporaryStep({
              step: newValues,
              groupId: stepGroupId!,
              atIndex,
            });
          } else {
            await saveStep({
              id: stepId,
              data: newValues,
            });
          }
          resetForm({ values: newValues });
        } catch (saveError) {
          let errors: Record<string, string> = { unexpectedError: (saveError as Error).message };

          if (isBadRequestError(saveError)) {
            const extractedErrors = getExtractedErrorMessages(saveError);
            if (extractedErrors?.file) {
              errors = { value: extractedErrors.file };
            } else {
              errors = getExtractedErrorMessages(saveError);
            }
          }

          resetForm({ values: newValues, errors });
        }
      },
      [isTemporary, saveStep, stepId, stepGroupId, saveTemporaryStep, atIndex],
    );

    const initialValues = useMemo(() => (step ? getInitialValues<typeof step>(step) : {}), [step]);

    if (!isTemporary) {
      if (isLoading) {
        return (
          <S.LoaderContainer>
            <Loader size="large" />
            <span>{t('loading', 'Gathering step details...')}</span>
          </S.LoaderContainer>
        );
      }

      if (isError || !step) {
        console.error(
          'Error while fetching step details',
          stepId,
          Object.values(getExtractedErrors(error) ?? {}),
        );
        return <ServerErrorInfo isVisible onRetry={refetch} />;
      }
    }

    const isNotEditable =
      isFreePlan &&
      ([STEP_TYPE.EXECUTE, STEP_TYPE.SET_LOCAL_VARIABLE] as Step['type'][]).includes(step.type);
    const isReadOnly = readOnly || isNotEditable;

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getValidationSchema}
        onSubmit={handleSubmit}
        validateOnMount={false}
        validateOnBlur={false}
        validateOnChange={false}
      >
        <StepDetailsContent
          context={context}
          step={step!}
          testRunId={testRunId}
          onClose={onClose}
          temporary={isTemporary}
          readOnly={isReadOnly}
        />
      </Formik>
    );
  },
);

interface StepDetailsContentProps extends Omit<StepDetailsProps, 'stepId' | 'groupId'> {
  step: Step | TemporaryStep;
  temporary: boolean;
}

const StepDetailsContent = ({
  context,
  step,
  onClose,
  readOnly,
  temporary,
}: StepDetailsContentProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLFormElement>(null);
  const modal = useModal();
  const stepRun = useAppSelector(selectStepRun(step.id, step.groupId)) as unknown as
    | StepRun
    | undefined;
  const { setSubmitting, handleSubmit, isSubmitting, dirty, errors } = useFormikContext();

  const handleOutsideClick = useCallback(
    (event) => {
      if (
        (dirty || temporary) &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        // is not a ariakit backdrop
        !document.querySelector('[data-backdrop]')?.contains(event.target) &&
        // is not a ariakit modal
        !document.querySelector('[data-dialog]')?.contains(event.target) &&
        // is not a modal
        !document.querySelector('.backdrop')?.contains(event.target) &&
        // is not a dropdown
        !document.querySelector('#layers')?.contains(event.target) &&
        // test step side panel resizer
        !document.querySelector('.Resizer')?.contains(event.target)
      ) {
        event.preventDefault();
        event.stopPropagation();
        modal.show('unsaved_changes', {
          onSave: handleSubmit,
          onDiscard: onClose,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSubmit, onClose, dirty, temporary],
  );

  const handleClick = useCallback(() => handleSubmit(), [handleSubmit]);

  useLeavePagePrompt(dirty, STEP_UNSAVED_CHANGES);

  useEvent('mousedown', handleOutsideClick, document, true);

  useUpdateEffect(() => setSubmitting(false), [step.id]);

  return (
    <S.Container data-testid="StepDetailsContent" ref={containerRef}>
      <Prompt message={STEP_UNSAVED_CHANGES} when={!!temporary || dirty} />
      <S.Header>
        <S.TitleContainer>
          <S.Title as="h2">{t('stepDetails.header.title', 'Step details')}</S.Title>
          <S.ActionButton onClick={onClose} disabled={isSubmitting} bordered>
            {t('default.button.cancel')}
          </S.ActionButton>
          {!readOnly && (
            <>
              <S.ActionButton
                key={step.id}
                onClick={handleClick}
                pending={isSubmitting}
                disabled={isSubmitting}
                succeeded={!isSubmitting && !Object.keys(errors).length}
              >
                {temporary ? t('default.button.create') : t('default.button.save')}
              </S.ActionButton>
              <Tooltip
                anchor="bottom-end"
                content={
                  readOnly || temporary
                    ? t(
                        'stepDetails.moreActions.temporaryStepTooltip',
                        'You need to create this step until actions are available',
                      )
                    : undefined
                }
              >
                <S.MoreActions
                  stepId={step.id}
                  groupId={step.groupId}
                  disabled={readOnly || temporary}
                  active={step.isActive}
                  bordered
                />
              </Tooltip>
            </>
          )}
        </S.TitleContainer>
        <StepRunMetadata step={step} />
      </S.Header>
      <S.PaidFeatureBanner messageFor={step.type} />
      <StepDetailsEditor
        step={step}
        context={context}
        readOnly={readOnly}
        failedStep={!!stepRun && isFailedStatus(stepRun.status)}
      />
    </S.Container>
  );
};

StepDetailsContent.displayName = 'StepDetailsContent';

StepDetails.displayName = 'StepDetails';
